import { useEffect } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { Box as DarkBox } from '@hivewatch/dark'

import { DeviceFeed } from '@/features/device-feed'
import {
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
} from '@/features/video-player'
import { useDevicePopoverDetailsQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'
import { AnalyticsScreen, useMixpanel } from '@/utils/analytics'

import { DarkFloorPlanDeviceMarker } from './DarkFloorPlanDeviceMarker'
import { darkPopoverStyles } from './DarkFloorPlanPopover.styles'

interface DarkDevicePopoverContentIProps {
  colorScheme: 'dark' | 'light'
  deviceId: string
  deviceName: string
  deviceType: DeviceType
}

export const DarkDevicePopoverContent = ({
  colorScheme,
  deviceId,
  deviceName,
  deviceType,
}: DarkDevicePopoverContentIProps) => {
  const isCameraDevice = deviceType === DeviceType.Camera
  const s = darkPopoverStyles(colorScheme)

  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )

  const { data, loading: isLoading } = useDevicePopoverDetailsQuery({
    nextFetchPolicy: 'cache-first',
    variables: { id: deviceId },
  })

  const { createCameraEventHandler, markStreamRequested } = useMixpanel(
    AnalyticsScreen.DeviceHoverModal
  )

  useEffect(() => {
    markStreamRequested()
  }, [])

  const mixpanelData = () => ({
    device_id: deviceId,
    facility_id: data?.device?.facility?.id,
    floor_id: data?.device?.floor?.id,
    floor_name: data?.device?.floor?.name,
    streaming_type: data?.device?.camera?.streamingType,
  })

  const handleCameraEvent = createCameraEventHandler(mixpanelData())

  const {
    stream: { url: cameraFeedUrl, isLoading: isFeedDataLoading, errorMessage },
    nextCamera,
    previousCamera,
    availableCamerasIds,
    currentCameraId,
  } = useFindDeviceFeed(deviceId, handleCameraEvent)

  return (
    <DarkBox
      data-testid='DarkDevicePopoverContent:wrapper'
      display='flex'
      flexDirection='column'
    >
      {isCameraDevice && (
        <DarkBox {...s.feedFrame}>
          <DeviceFeed
            isLoading={isFeedDataLoading || isLoading}
            onClickNext={nextCamera}
            onClickPrevious={previousCamera}
            showCameraSwitcher={availableCamerasIds.length > 1}
          >
            {isMSEStreamingEnabled && currentCameraId ? (
              <MSEVideoPlayer
                autoplay={true}
                cameraId={currentCameraId}
                errorLabel={errorMessage}
                onEvent={handleCameraEvent}
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                placeholderHeight='204px'
                showControls={true}
              />
            ) : (
              <VideoPlayer
                autoplay={true}
                errorLabel={errorMessage}
                liveEnabled={true}
                liveOnly
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                pipActive={false}
                placeholderHeight='204px'
                url={cameraFeedUrl}
              />
            )}
          </DeviceFeed>
        </DarkBox>
      )}
      <DarkBox {...s.deviceRow}>
        <DarkFloorPlanDeviceMarker
          colorScheme={colorScheme}
          deviceType={deviceType}
        />
        <DarkBox {...s.deviceName}>{deviceName}</DarkBox>
      </DarkBox>
    </DarkBox>
  )
}
